import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope.ts";
import { useGetOrgData } from "~hooks/useGetOrgData";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { filterCompetitions } from "~pages/Competitions/CompetitionsListPage/utils/filterCompetitions.ts";
import type { CompetitionTransformed } from "~pages/Competitions/types/competitionTransformed.ts";
import useDeleteCompetition from "../hooks/useDeleteCompetition.ts";
import { competitionsExportFields, defaultCompetitionsExportFieldNames } from "./constants/exportFields.ts";
import { useExportCompetitions } from "./hooks/useExportCompetitions.ts";
import { useGetCompetitionsTableColumns } from "./hooks/useGetCompetitionsTableColumns.tsx";
import { useGetCompetitionsTableFilters } from "./hooks/useGetCompetitionsTableFilters.tsx";
import { useImportCompetitions } from "./hooks/useImportCompetitions.ts";

const CompetitionsList = () => {
  const { sport, organizationId, customerId } = useGetOrgData();
  const location = useLocation();
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteCompetition();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(50, 1, [50, 100, 500, 1000]);
  const { formatMessage } = useIntl();

  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { scope, resources } = scopeData || {};

  const { mutateAsync: startImporting } = useImportCompetitions();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportCompetitions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableImportConfiguration = uiConfiguration.pages.competitions.listPage.importConfig;
  const dataTableUiConfiguration = uiConfiguration.pages.competitions.listPage;

  const allCompetitions = useMemo(() => {
    const competitionResources = resources?.competitions || {};
    const leagueResources = resources?.leagues || {};
    const competitionsList: CompetitionTransformed[] = [];

    scope?.leagueTree?.forEach(({ competitions }) => {
      competitionsList.push(
        ...competitions.map(({ competitionId }) => {
          const leagueId = competitionResources[competitionId].leagueId;
          return { ...competitionResources[competitionId], league: leagueId ? leagueResources[leagueId] : null };
        }),
      );
    });
    return competitionsList;
  }, [scope?.leagueTree, resources?.competitions, resources?.leagues]);

  const handleDelete = useCallback(
    async (competitionId: string) => {
      return await deleteFn(competitionId);
    },
    [deleteFn],
  );

  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/manage/`);

  const columns = useGetCompetitionsTableColumns();
  const filtersConfig = useGetCompetitionsTableFilters();

  const clientCompetitions = filterCompetitions(allCompetitions, filtersConfig.currentFilter, searchText);
  const queryClient = useQueryClient();

  const refetch = async () => {
    await queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user", sport, organizationId] });
  };

  return (
    <div>
      <DataTable
        dataTableUiConfiguration={dataTableUiConfiguration}
        title={formatMessage({ id: "competitionsListPage.title" })}
        handleRefresh={() => {
          refetch();
        }}
        columns={columns}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        rowActions={{
          base: {
            manage: {
              onClick: (row: CompetitionTransformed) =>
                navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/manage/${row.competitionId}`),
            },
            delete: {
              onClick: (row: CompetitionTransformed) => handleDelete(row.competitionId),
              loading: isDeletePending,
            },
          },
        }}
        handleAdd={handleAdd}
        addButtonIntlKey={"competition"}
        isDataLoading={isLoading}
        filtersConfig={filtersConfig}
        rowKey={"competitionId"}
        rowNameKey={"nameLocal"}
        paginationConfig={{ ...paginationConfig, total: clientCompetitions.length }}
        dataSource={clientCompetitions}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultCompetitionsExportFieldNames,
          groupedFields: competitionsExportFields,
          localStorageKey: "export.competitions",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.competitions`}
        columnSortConfig={{
          sorterLocalStorageKey: "tables.settings.sort.competitions",
          defaultSorter: {
            columnKey: "nameLocal",
            order: "ascend",
          },
        }}
        permissionNames={[PermissionNames.COMPETITION]}
      />
    </div>
  );
};

export default CompetitionsList;
